import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Icon } from '@iq/react-components';
import { capitalize, extractCategories } from '../../../../../utils';
import Delete from '../../../../Delete';
import JsonEditor from '../../../../JsonEditor';
import ItemHandler from '../ItemHandler';
import TypeLabel from '../../../../TypeLabel';
import StyledItem from '../../../../StyledItem';
import config from '../../../../../config';

import {
  getDefaultPanelType,
  getExtendedPanelSchema,
  getFilteredPanelTypes,
  uniquePerPagePanelTypes,
  widePanelTypes,
  fitContentOnlyPanelTypes,
} from '../../../../panels/panelSchema';
import { getComponentsPanelSchema } from '../../../../panels/ComponentsPanel';
import { getFilesPanelSchema } from '../../../../panels/FilesPanel';
import { textPanelSchema } from '../../../../panels/TextPanel';
import { tfrPanelSchema } from '../../../../panels/TfrPanel';
import {
  getVisualizationsPanelSchema,
  visualizationsPanelOptionsUiSchema,
} from '../../../../panels/VisualizationsPanel';
import { healthPanelSchema } from '../../../../panels/HealthPanel';
import { getComponentAttributesPanelSchema } from '../../../../panels/ComponentAttributesPanel';
import {
  getAnalyticsTablePanelSchema,
  getSwitchgearCounterSources,
  shouldShowAnalyticsTablePanelType,
} from '../../../../panels/AnalyticsTablePanel';
import { shouldKeepAnalyticsTableComponentScope } from '../../../../panels/AnalyticsTablePanel/sourceUtils';

import { requestTypes, getTypes } from '../../../../../bundles/types';
import { requestTags, getTags } from '../../../../../bundles/tags';
import { getStaticComponents } from '../../../../../bundles/components';
import { getVisualizations } from '../../../../../bundles/visualizations';
import { getActiveSite } from '../../../../../bundles/sites';
import { getUserEventTypes, requestUserEventTypes } from '../../../../../bundles/events';
import { getSources } from '../../../../../bundles/sources';
import { getPagePanelTypeCounts } from '../../../../../bundles/layouts/selectors';
import { getEventTimeLinePanelSchema } from '../../../../panels/EventTimelinePanel/jsonSchema';
import { LIMITED_LIST_VIEW } from '../../../../panels/EventTimelinePanel/EventTimelinePanel';
import { getAlarmPanelSchema } from '../../../../panels/AlarmPanel';

// To remove duplicate error message
const transformErrors = (errors) =>
  errors.map((error) => {
    const errorName = error.name === 'required' ? error.name : error.property;
    switch (errorName) {
      case 'required': {
        if (error.property === 'title') {
          error.message = '';
        }
        break;
      }
      case '.type': {
        error.message = '';

        break;
      }
      case '.options.filters.eventTypes.types': {
        if (error.name === 'minItems') {
          error.message = '';
        }

        break;
      }
      case '.options.views': {
        if (error.name === 'minItems') {
          error.message = '';
        }

        break;
      }
      case '.options.defaultView': {
        error.message = '';

        break;
      }
      default:
    }
    return error;
  });
const ManagePanels = ({
  activeTree = {},
  panels = [],
  updatePanel,
  movePanel,
  removePanel,
  columnActive = false,
  setActiveTreeIndexes,
  columnCount = 1, // Number of columns in the section
  columnIndex = 0, // Index of the column
  sectionHeight = 'fit-content', // Section height for filtering panel types
}) => {
  const dispatch = useDispatch();
  const panelReference = useRef(null);
  const { id: siteId } = useSelector(getActiveSite);

  const types = useSelector(getTypes);
  const tags = useSelector(getTags);
  const visualizations = useSelector(getVisualizations);
  const stateComponents = useSelector(getStaticComponents);
  const eventTypes = useSelector(getUserEventTypes);
  const sources = useSelector(getSources);
  const pagePanelTypeCounts = useSelector((state) =>
    getPagePanelTypeCounts(state, siteId, activeTree.page)
  );

  const switchgearSources = useMemo(() => getSwitchgearCounterSources(sources), [sources]);
  const showAnalyticsTable = useMemo(
    () => shouldShowAnalyticsTablePanelType(sources, config.onPremise),
    [sources]
  );

  const { panel: activePanelIndex = null } = activeTree;

  useEffect(() => {
    dispatch(requestTypes(siteId));
    dispatch(requestTags(siteId));
    dispatch(requestUserEventTypes());
  }, [dispatch, siteId]);

  const activePanel = panels[activePanelIndex];
  const components = stateComponents;
  const activePanelOptions = useMemo(() => activePanel?.options || {}, [activePanel?.options]);
  const activePanelTabGroups = useMemo(
    () => activePanelOptions.tabGroups || [],
    [activePanelOptions]
  );

  const updatedFilesPanelSchema = useMemo(
    () => getFilesPanelSchema(types || [], tags || [], activePanelOptions),
    [types, tags, activePanelOptions]
  );

  const updatedEventTimelineSchema = useMemo(
    () => getEventTimeLinePanelSchema(eventTypes || [], activePanelOptions),
    [eventTypes, activePanelOptions]
  );

  const updatedVisualizationsPanelSchema = useMemo(
    () =>
      getVisualizationsPanelSchema(
        visualizations,
        tags || [],
        activePanelTabGroups,
        activePanelOptions.showTabs
      ),
    [visualizations, tags, activePanelTabGroups, activePanelOptions.showTabs]
  );

  const updatedComponentsPanelSchema = useMemo(
    () => getComponentsPanelSchema(components),
    [components]
  );

  const vizPanel = activePanel?.type === 'visualizations';
  let vizPanelSettings;

  if (vizPanel) {
    const {
      options: { visualizations: panelVisualizations = [], tabGroups: panelTabGroups = [] } = {},
    } = activePanel;

    const vizNames = new Set(visualizations.map((v) => v.name));
    const tagIds = new Set(tags.map((t) => t.id));

    const updateNames = panelVisualizations.filter((name) => vizNames.has(name));

    // Filter out deleted tags from tabGroups
    const updateTabGroups = panelTabGroups
      .map((tabGroup) => ({
        ...tabGroup,
        tags: tabGroup.tags?.filter((tagId) => tagIds.has(tagId)) || [],
      }))
      .filter((tabGroup) => tabGroup.tags.length > 0);

    vizPanelSettings = {
      ...activePanel,
      options: {
        ...activePanel.options,
        visualizations: updateNames,
        tabGroups: updateTabGroups,
      },
    };
  }

  const updatedComponentAttributesPanelSchema = useMemo(() => {
    const categories = extractCategories(components);
    return getComponentAttributesPanelSchema(categories);
  }, [components]);

  const updatedAnalyticsTablePanelSchema = useMemo(
    () => getAnalyticsTablePanelSchema(switchgearSources, config.onPremise),
    [switchgearSources]
  );

  const extendedPanels = useMemo(() => {
    const basePanels = {
      alarm: getAlarmPanelSchema(),
      components: updatedComponentsPanelSchema,
      'event-timeline': updatedEventTimelineSchema,
      text: textPanelSchema,
      visualizations: updatedVisualizationsPanelSchema,
      health: healthPanelSchema,
      'component-properties': updatedComponentAttributesPanelSchema,
    };

    if (showAnalyticsTable) {
      basePanels['analytics-table'] = updatedAnalyticsTablePanelSchema;
    }

    if (!config.onPremise) {
      basePanels.files = updatedFilesPanelSchema;
      basePanels['tfr-viewer'] = tfrPanelSchema;
    }

    return basePanels;
  }, [
    updatedComponentsPanelSchema,
    updatedEventTimelineSchema,
    updatedFilesPanelSchema,
    updatedVisualizationsPanelSchema,
    updatedComponentAttributesPanelSchema,
    updatedAnalyticsTablePanelSchema,
    showAnalyticsTable,
  ]);

  const filteredPanelTypes = useMemo(() => {
    const excludeTypes = [];

    uniquePerPagePanelTypes.forEach((panelType) => {
      const panelTypeCount = pagePanelTypeCounts.get(panelType) ?? 0;

      if (panelTypeCount > 0 && activePanel?.type !== panelType) {
        excludeTypes.push(panelType);
      }
    });

    if (config.onPremise) {
      excludeTypes.push('files', 'tfr-viewer', 'health');
    }
    if (!showAnalyticsTable) {
      excludeTypes.push('analytics-table');
    }
    return getFilteredPanelTypes(excludeTypes);
  }, [activePanel?.type, pagePanelTypeCounts, showAnalyticsTable]);

  const defaultPanelType = useMemo(
    () => getDefaultPanelType(filteredPanelTypes),
    [filteredPanelTypes]
  );

  const activePanelFormData = useMemo(() => {
    const panel = vizPanel ? vizPanelSettings : activePanel;

    if (!panel) {
      return panel;
    }

    if (panel.type) {
      return panel;
    }

    return {
      ...panel,
      title: panel.title || 'Untitled panel',
      type: defaultPanelType,
    };
  }, [activePanel, defaultPanelType, vizPanel, vizPanelSettings]);

  const updatedPanelSchema = useMemo(
    () =>
      getExtendedPanelSchema(extendedPanels, components, filteredPanelTypes, {
        columnCount,
        columnIndex,
        sectionHeight,
      }),
    [extendedPanels, components, filteredPanelTypes, columnCount, columnIndex, sectionHeight]
  );

  useEffect(() => {
    if (columnActive && panelReference.current && activePanelIndex !== null) {
      panelReference.current.scrollIntoView();
    }
  }, [columnActive, activePanelIndex]);

  const togglePanel = (panelIndex) => {
    if (activePanelIndex === panelIndex) {
      setActiveTreeIndexes({ ...activeTree, panel: undefined, initial: false });
    } else {
      setActiveTreeIndexes({ ...activeTree, panel: panelIndex, initial: false });
    }
  };

  const onClickMovePanel = (from, to) => {
    movePanel(from, to);
    setActiveTreeIndexes({ ...activeTree, panel: to, initial: false });
  };

  const onUpdatePanel = useCallback(
    ({ formData }) => {
      let updatedFormData = { ...formData };

      //Panel types are now filtered in dropdown based on section constraints

      if (updatedFormData.panelScope?.selectedScope === 'all') {
        updatedFormData = {
          ...updatedFormData,
          panelScope: {
            selectedScope: 'all',
          },
        };
      }

      if (
        updatedFormData.type === 'analytics-table' &&
        !shouldKeepAnalyticsTableComponentScope(updatedFormData.tableType)
      ) {
        updatedFormData = { ...updatedFormData };
        delete updatedFormData.componentScope;
      }

      updatePanel(activePanelIndex, updatedFormData);
    },
    [updatePanel, activePanelIndex]
  );

  const customValidate = useCallback(
    (formData, errors) => {
      for (const fieldName of updatedPanelSchema.required) {
        const validValue = formData[fieldName];
        if (!validValue) {
          errors[fieldName].addError(`Panel ${capitalize(fieldName)} is required`);
        }
      }

      if (formData.type === 'files') {
        const views = formData.options?.views ?? [];
        if (views.length === 0) {
          errors.options.views.addError('Select at least one view.');
        } else if (!views.includes(formData.options?.defaultView ?? 'grid')) {
          errors.options.defaultView.addError('Default view must be one of the selected views.');
        }
      }

      if (formData.type === 'event-timeline') {
        const views = formData.options?.views ?? [];
        if (views.includes(LIMITED_LIST_VIEW) && views.some((v) => v !== LIMITED_LIST_VIEW)) {
          errors.options.views.addError(
            'Limited list view cannot be combined with other views. Deselect it or remove the other views.'
          );
        }

        if ((formData.options?.filters?.eventTypes?.types?.length ?? 0) === 0) {
          errors?.options?.filters?.eventTypes?.types.addError('Select at least one event type.');
        }
      }

      return errors;
    },
    [updatedPanelSchema]
  );

  return (
    <div
      className="manage-panels-component"
      ref={panelReference}
    >
      <div className="panel-list">
        <div className="panel-handles">
          {panels.map((panel, panelIndex) => (
            <StyledItem
              key={panel.id}
              itemClass={`panel-handle ${activePanelIndex === panelIndex ? 'selected' : ''}`}
              onClick={() => togglePanel(panelIndex)}
              headerLabel={
                <TypeLabel
                  type="panel"
                  text={panel.type}
                />
              }
              headerContent={
                <div className="title">
                  {panel.title}
                  {/* Visual indicators for panel constraints */}
                  {panel.type && widePanelTypes.has(panel.type) && (
                    <span
                      style={{ marginLeft: '0.5rem', fontSize: '0.8em', opacity: 0.7 }}
                      title="This panel requires wide columns"
                    >
                      [Wide Column]
                    </span>
                  )}
                  {panel.type && fitContentOnlyPanelTypes.has(panel.type) && (
                    <span
                      style={{ marginLeft: '0.5rem', fontSize: '0.8em', opacity: 0.7 }}
                      title="This panel only works with Fit Content section height"
                    >
                      [Fit Content Only]
                    </span>
                  )}
                </div>
              }
              headerActions={
                <>
                  <ItemHandler
                    move={onClickMovePanel}
                    index={panelIndex}
                    count={panels.length}
                  />
                  <Icon icon="keyboard-arrow-right" />
                </>
              }
            />
          ))}
        </div>
        <div className="panel-editor">
          {columnActive && activePanelIndex !== null ? (
            <>
              {panels.map((panel, panelIndex) => (
                <div key={panel.id}>
                  {panelIndex === activePanelIndex ? (
                    <JsonEditor
                      title="Panel properties"
                      context={panels[activePanelIndex]}
                      formData={activePanelFormData}
                      schema={updatedPanelSchema}
                      uiSchema={{ options: visualizationsPanelOptionsUiSchema }}
                      onFormSubmit={onUpdatePanel}
                      saveButtonText={'Update panel properties'}
                      customValidate={customValidate}
                      customTransformErrors={(errors) => transformErrors(errors)}
                      editorOnly
                    />
                  ) : null}
                </div>
              ))}
              <Delete
                onDelete={() => {
                  setActiveTreeIndexes({ ...activeTree, panel: undefined, initial: false });
                  removePanel(activePanelIndex);
                }}
                title="Delete panel"
              />
            </>
          ) : (
            <div className="empty">Create or select a panel</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagePanels;
